<template><div><h1 id="_2-document-output" tabindex="-1"><a class="header-anchor" href="#_2-document-output"><span>2. Document Output</span></a></h1>
<h2 id="_2-1-einfuhrung" tabindex="-1"><a class="header-anchor" href="#_2-1-einfuhrung"><span>2.1. Einführung</span></a></h2>
<p>Mit den <em>Document Output</em>-Funktionen können aus funtrade-Daten formatierte Outputs erstellt werden. Das System unterstützt PDFs, HTML E-Mails (mit PDF Attachments) und DOCX (Microsoft Word) als Ausgabeformate.</p>
<p>Früher wurde für dieses System auch die Begriffe <code v-pre>Print-Output</code> oder <code v-pre>XML-Output</code> verwendet.</p>
<h3 id="_2-1-1-begriffe" tabindex="-1"><a class="header-anchor" href="#_2-1-1-begriffe"><span>2.1.1. Begriffe</span></a></h3>
<p><code v-pre>Vorlage</code>: Eine <RouteLink to="/referenz/refs/ft.refdata.do-vorlagen.html" title="Vorlagen">Vorlage</RouteLink> bestimmt das Aussehen von Dokumenten. Es definiert eine Menge von Platzhaltern.</p>
<p><code v-pre>Dokument</code>: Ein Dokument basiert auf einer <RouteLink to="/referenz/refs/ft.refdata.do-vorlagen.html" title="Vorlagen">Vorlage</RouteLink> und definiert, welche Platzhalter (aus der <RouteLink to="/referenz/refs/ft.refdata.do-vorlagen.html" title="Vorlagen">Vorlage</RouteLink> ) mit welchen Texten oder Bildern gefüllt werden sollen.</p>
<p><code v-pre>Attribut</code>
Ein Dokument kann für einen Platzhalter verschiedene Inhalte abfüllen, je nach Wert von bestimmten Attributen (z.B. Dokumentencode, Warnstufe).</p>
<p><code v-pre>Dokumentensatz</code>
Dokumentensätze bestehen aus einem oder mehreren Dokumenten, die jeweils zusammen erzeugt werden. Z.B. können das für Zusagen-Interessenten 3 Dokumente sein, nämlich ein Brief, 1 Zusagenformular und 1 Einzahlungsschein.</p>
<h3 id="_2-1-2-funktionsprinzip" tabindex="-1"><a class="header-anchor" href="#_2-1-2-funktionsprinzip"><span>2.1.2. Funktionsprinzip</span></a></h3>
<p>Das grundsätzliche Funktionsprinzip ist wie folgt:</p>
<p><strong>Abbildung 7.1. Ablauf Document Output</strong></p>
<p><img src="@source/manual/administration/i/2_xml-output_overview.jpg" alt="Ablauf Document Output"></p>
<ol>
<li>
<p>Die Rohdaten werden (im XML-Format) exportiert und in einer Tabelle gespeichert.</p>
</li>
<li>
<p>Der Output wird nach den Definitionen in der <RouteLink to="/referenz/refs/ft.refdata.do-vorlagen.html" title="Vorlagen">Vorlage</RouteLink> (hier XML-Stylesheet (XSLT) erstellt und ebenfalls gespeichert.</p>
</li>
<li>
<p>Der Output wird geladen und z.B. auf den Drucker ausgegeben.</p>
</li>
</ol>
<h3 id="_2-1-3-ablauf-typ-pdf-und-html" tabindex="-1"><a class="header-anchor" href="#_2-1-3-ablauf-typ-pdf-und-html"><span>2.1.3. Ablauf Typ PDF- und HTML</span></a></h3>
<p>Der grundsätzliche Ablauf für die Erstellung eines Dokumentes und Produktion ist hier aufgezeigt. Bedingung ist, das Arenae ein Stylesheet für das Dokument vorbereitet hat.</p>
<ol>
<li>
<p><RouteLink to="/referenz/refs/ft.refdata.do-dokumente.html" title="Dokumente, Stammtabelle">Dokument</RouteLink> erfassen und den <RouteLink to="/referenz/refs/ft.refdata.do-dokumente.html#do-platzhalter">Platzhaltern</RouteLink> die <RouteLink to="/referenz/refs/ft.refdata.do-text.html" title="Text- und Bildelemente">Text- und Bildelemente</RouteLink> zuweisen.</p>
</li>
<li>
<p><RouteLink to="/referenz/refs/ft.refdata.do-doksatz.html" title="Dokumententensatz">Dokumentensatz</RouteLink> mit den zugehörigen <RouteLink to="/referenz/refs/ft.refdata.do-dokumente.html" title="Dokumente, Stammtabelle">Dokumenten</RouteLink> erfassen.</p>
<p>Nun ist auf Seite Document Output alles für die Verwendung vorbereitet. Zukünftig können bei Bedarf Dokumente gelöscht, ergänzt oder auch nur die Texte angepasst werden.</p>
</li>
<li>
<p>Der Dokumentensatz kann nun in verschiedenen Anwendungen, wie z.B. Verdankungen, Zusagenfakturierung und Korrespondenzen für die Produktion der entsprechenden Dokumente eingesetzt werden.</p>
</li>
<li>
<p>In der <RouteLink to="/referenz/refs/ft.refdata.do-outputverwaltung.html" title="Outputverwaltung">Outputverwaltung</RouteLink> sind die erstellten Outputs aus allen Anwendungen nach Datum aufgeführt und können hier auch geöffnet werden.</p>
</li>
</ol>
<h3 id="_2-1-4-ablauf-word-dokumente" tabindex="-1"><a class="header-anchor" href="#_2-1-4-ablauf-word-dokumente"><span>2.1.4. Ablauf Word-Dokumente</span></a></h3>
<p>Die Vorlagen für die Word-Dokumente können durch den User selbst, ohne vorgängige Stylesheet-Programmierung, erstellt werden. Dafür wird ein MS-Word-Dokument mit markierten Texten/Textbereichen, die bei der Dokument-Produktion durch die <RouteLink to="/referenz/refs/ft.refdata.do-dokumente.html#do-platzhalter">Platzhalter</RouteLink> in funtrade ersetzt werden, verwendet. Der grundsätzliche Ablauf für die Erfassung und Produktion ist hier aufgezeigt:</p>
<ol>
<li>
<p>Ein Dokument in MS-Word erfassen. Es reicht, für Texte/Textbereiche, die später mit <RouteLink to="/referenz/refs/ft.refdata.do-dokumente.html#do-platzhalter">Platzhaltern</RouteLink> ersetzt werden, ein einzelnes Wort (am besten mit der Platzhalterbezeichnung wie z.B. &quot;Text_01&quot;) einzufügen.</p>
<p>Danach werden die zu ersetzenden Texte/Textbereiche einzeln selektiert (... aber nicht das Paragraph-Zeichen!) und je eine Textmarke (Einfügen - Textmarke) hinzugefügt. Die Textmarke muss gleich heissen wie der <RouteLink to="/referenz/refs/ft.refdata.do-dokumente.html#do-platzhalter">Platzhalter</RouteLink> im <RouteLink to="/referenz/refs/ft.refdata.do-dokumente.html" title="Dokumente, Stammtabelle">Dokument</RouteLink> .</p>
<div class="hint-container warning">
<p class="hint-container-title">Wichtig</p>
<p>Schriftart und Formatierung werden nur in das <RouteLink to="/referenz/refs/ft.refdata.do-dokumente.html" title="Dokumente, Stammtabelle">Dokument</RouteLink> übernommen, wenn die Texte in MS-Word über Formatvorlagen formatiert worden sind!</p>
</div>
</li>
<li>
<p>Unter <RouteLink to="/referenz/refs/ft.refdata.do-vorlagen.html" title="Vorlagen">Vorlagen</RouteLink> eine neue Vorlage erfassen. In funtrade sind Mustervorlagen für verschiedene Geschäfstfälle erfasst. Bei &quot;Kopie von:&quot; darauf achten, dass der Geschäftsfall mit der kopierten <RouteLink to="/referenz/refs/ft.refdata.do-vorlagen.html" title="Vorlagen">Vorlage</RouteLink> übereinstimmt.</p>
</li>
<li>
<p>Auf der <RouteLink to="/referenz/refs/ft.refdata.do-vorlagen.html" title="Vorlagen">Vorlage</RouteLink> mit &quot;Kontextmenü - &quot;Vorlage hochladen...&quot; das MS-Word-Dokument in die <RouteLink to="/referenz/refs/ft.refdata.do-vorlagen.html" title="Vorlagen">Vorlage</RouteLink> importieren.</p>
</li>
<li>
<p>Neues <RouteLink to="/referenz/refs/ft.refdata.do-dokumente.html" title="Dokumente, Stammtabelle">Dokument</RouteLink> erfassen und die dafür erstellte <RouteLink to="/referenz/refs/ft.refdata.do-vorlagen.html" title="Vorlagen">Vorlage</RouteLink> übernehmen. Nun erscheinen die <RouteLink to="/referenz/refs/ft.refdata.do-dokumente.html#do-platzhalter">Platzhalter</RouteLink> im Dokument.</p>
</li>
<li>
<p>Die <RouteLink to="/referenz/refs/ft.refdata.do-dokumente.html#do-platzhalter">Platzhalter</RouteLink> mit den gewünschten <RouteLink to="/referenz/refs/ft.refdata.do-text.html" title="Text- und Bildelemente">Text- und Bildelementen</RouteLink> verknüpfen.</p>
</li>
<li>
<p><RouteLink to="/referenz/refs/ft.refdata.do-doksatz.html" title="Dokumententensatz">Dokumentensatz</RouteLink> mit den zugehörigen <RouteLink to="/referenz/refs/ft.refdata.do-dokumente.html" title="Dokumente, Stammtabelle">Dokumenten</RouteLink> erfassen.</p>
</li>
</ol>
<h3 id="_2-1-5-dokument-definitionen" tabindex="-1"><a class="header-anchor" href="#_2-1-5-dokument-definitionen"><span>2.1.5. Dokument Definitionen</span></a></h3>
<p>Die <RouteLink to="/referenz/refs/ft.refdata.dokdefinitionen.html" title="Dokument Definitionen">Dokument Definitionen</RouteLink> erlauben ein direkte Erfassung/Änderung von <RouteLink to="/referenz/refs/ft.refdata.do-dokumente.html" title="Dokumente, Stammtabelle">Dokumenten</RouteLink>, direkt in den den Stammtabellen der Prozesse. So können Dokumente bearbeitet werden, ohne die Ansicht zu wechseln. Umgesetzt ist das in folgenden Tabellen:</p>
<p><RouteLink to="/referenz/refs/ft.refdata.vdanksteuer.html" title="Verdankungssteuertabelle">Verdankungssteuertabelle</RouteLink></p>
<h3 id="_2-1-6-outputverwaltung" tabindex="-1"><a class="header-anchor" href="#_2-1-6-outputverwaltung"><span>2.1.6. Outputverwaltung</span></a></h3>
<p>Über die <RouteLink to="/referenz/refs/ft.refdata.do-outputverwaltung.html" title="Outputverwaltung">Outputverwaltung</RouteLink> können noch nicht abgeschlossene Jobs weiterverarbeitet werden (z.B. Erstellen von Dokumenten aus Rohdaten oder Druck von erstellten PDF-Files). Es werden zu jedem Job die Dokumentenempfänger mit den zugehörigen Dokumenten angezeigt.</p>
<p>In funtrade werden die erstellten Dokumente unter Personen im Tab &quot;Dokumente&quot; angezeigt. Sie können mit der Funktion &quot;Dokument anzeigen&quot; (im Menü) geöffnet werden.</p>
<h2 id="_2-2-geschaftsfalle-und-verfugbare-felder" tabindex="-1"><a class="header-anchor" href="#_2-2-geschaftsfalle-und-verfugbare-felder"><span>2.2. Geschäftsfälle und verfügbare Felder</span></a></h2>
<p>Document Output unterscheidet verschiedene Geschäftsfälle und bietet pro Geschäftsfall jeweils spezifische Datenfelder an. In diesem Abschnitt werden die Standard-Felder für die neueren Vorlagen beschrieben. Bei den älteren Vorlagen, die über Export-Formate gehen, stehen nur die Daten des jeweiligen Export Formats zur Verfügung.</p>
<p>Es gibt auch kundenspezifische Exportfelder, die hier nicht beschrieben sind.</p>
<p>Die Felder können in den Texten wie folgt verwendet werden:</p>
<div class="language-text line-numbers-mode" data-highlighter="prismjs" data-ext="text"><pre v-pre><code><span class="line">Für ihre Spende von &lt;fieldref name="Verdankung/EsrData1/Betrag/Wert" /> bedanken wir uns.</span>
<span class="line"></span></code></pre>
<div class="line-numbers" aria-hidden="true" style="counter-reset:line-number 0"><div class="line-number"></div></div></div><h3 id="_2-2-1-zahlen-und-datumsformate" tabindex="-1"><a class="header-anchor" href="#_2-2-1-zahlen-und-datumsformate"><span>2.2.1. Zahlen- und Datumsformate</span></a></h3>
<p>Numerische Felder und Datumsfelder können speziell formatiert werden.</p>
<p>Formate für Datumsfelder:</p>
<div class="language-text line-numbers-mode" data-highlighter="prismjs" data-ext="text"><pre v-pre><code><span class="line">default (ohne format) = 02. Februar 2015</span>
<span class="line">  zB. &lt;fieldref name="Verdankung/ExtraDatum1"/></span>
<span class="line">short = 2.2.2015</span>
<span class="line">  zB. &lt;fieldref name="Verdankung/ExtraDatum1" format="short"/></span>
<span class="line">medium = 02.02.2015</span>
<span class="line">  zB. &lt;fieldref name="Verdankung/ExtraDatum1" format="medium"/> </span>
<span class="line"></span></code></pre>
<div class="line-numbers" aria-hidden="true" style="counter-reset:line-number 0"><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div></div></div><p>Formate für numerische Felder:</p>
<div class="language-text line-numbers-mode" data-highlighter="prismjs" data-ext="text"><pre v-pre><code><span class="line">Für die Zahl 71234:</span>
<span class="line">default (ohne format): 71'234</span>
<span class="line">  zB. &lt;fieldref name="Verdankung/EsrData1/Betrag/Wert"/></span>
<span class="line">integer: 71234</span>
<span class="line">  zB. &lt;fieldref name="Verdankung/EsrData1/Betrag/Wert" format="integer"/></span>
<span class="line">currency = 71'234.00</span>
<span class="line">  zB. &lt;fieldref name="Verdankung/EsrData1/Betrag/Wert" format="currency"/></span>
<span class="line">format="##0.00": 71234.00</span>
<span class="line">  zB. &lt;fieldref name="Verdankung/EsrData1/Betrag/Wert" format="##0.00"/> </span>
<span class="line"></span></code></pre>
<div class="line-numbers" aria-hidden="true" style="counter-reset:line-number 0"><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div></div></div><h3 id="_2-2-2-allgemeine-felder-fur-alle-geschaftsfalle" tabindex="-1"><a class="header-anchor" href="#_2-2-2-allgemeine-felder-fur-alle-geschaftsfalle"><span>2.2.2. Allgemeine Felder für alle Geschäftsfälle</span></a></h3>
<p>Diese Felder sind immer verfügbar.</p>
<table>
<thead>
<tr>
<th>Feld</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>ADDRESS</td>
<td>Die                 formatierte Adresse des Empfängers mit unbeschränkt langen Zeilen (ohne Verkürzungen von funtrade).</td>
</tr>
<tr>
<td>SALUTATION</td>
<td>Die Anrede.</td>
</tr>
<tr>
<td>JobDatum</td>
<td>Das Datum, an dem die Rohdaten erstellt wurden.</td>
</tr>
</tbody>
</table>
<h3 id="_2-2-3-verdankung" tabindex="-1"><a class="header-anchor" href="#_2-2-3-verdankung"><span>2.2.3. Verdankung</span></a></h3>
<p>Die Verdankung hat folgende Felder:</p>
<table>
<thead>
<tr>
<th>Feld</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>Verdankung/EsrData1/Betrag/Wert</td>
<td>Der Betrag der Spende.</td>
</tr>
<tr>
<td>Verdankung/EsrData1/Betrag/Währung</td>
<td>Währung (z.B. &quot;CHF&quot;)</td>
</tr>
<tr>
<td>Verdankung/DokumentCode</td>
<td>Der Dokumentencode er wie in der Verdankungssteuertabelle definiert ist.</td>
</tr>
<tr>
<td>Verdankung/SpendeDatum</td>
<td>Das Datum der zu verdankenden Spende.</td>
</tr>
<tr>
<td>Verdankung/P2P-KampagneTitel</td>
<td>Text der Ereignisposition bei Anlassspenden (der Name P2P- ist etwas<br>ungeschickt hier, aber die peer-2-peer<br>Spenden gehen auch über Anlassspenden).</td>
</tr>
<tr>
<td>Verdankung/P2P-Initiators</td>
<td>Name der Kontaktperson wie z.B. &quot;Max Muster&quot;.</td>
</tr>
</tbody>
</table>
<h3 id="_2-2-4-korrespondenz" tabindex="-1"><a class="header-anchor" href="#_2-2-4-korrespondenz"><span>2.2.4. Korrespondenz</span></a></h3>
<p>Die Korrespondenz hat folgende Felder:</p>
<table>
<thead>
<tr>
<th>Feld</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>Korrespondenz/EsrData1/Betrag/Wert</td>
<td>Der Betrag zum Druck auf den ESR.</td>
</tr>
<tr>
<td>Korrespondenz/EsrData1/Betrag/Währung</td>
<td>Währung (z.B. &quot;CHF&quot;)</td>
</tr>
<tr>
<td>Korrespondenz/EsrData1/ESR</td>
<td>ESR Teilnehmer Nummer (5 stellig)</td>
</tr>
<tr>
<td>Korrespondenz/EsrData1/Konto</td>
<td></td>
</tr>
<tr>
<td>Korrespondenz/EsrData1/Refnum</td>
<td>Die Referenznummer</td>
</tr>
<tr>
<td>Korrespondenz/EsrData2/*</td>
<td>Ein optionaler 2. ESR analog zu EsrData1</td>
</tr>
<tr>
<td>Korrespondenz/DokumentCode</td>
<td>Der Dokumentencode wie in der Korrespondenzart definiert</td>
</tr>
<tr>
<td>Korrespondenz/Datum</td>
<td>Das Versanddatum der Korrespondenz (=JobDatum)</td>
</tr>
<tr>
<td>Korrespondenz/ExtraText1</td>
<td>Entsprechendes Feld auf der Korrespondenz</td>
</tr>
<tr>
<td>Korrespondenz/ExtraDatum1</td>
<td>Entsprechendes Feld auf der Korrespondenz</td>
</tr>
<tr>
<td>Korrespondenz/ExtraZahl1</td>
<td>Entsprechendes Feld auf der Korrespondenz</td>
</tr>
<tr>
<td>Korrespondenz/ExtraText2</td>
<td>Entsprechendes Feld auf der Korrespondenz</td>
</tr>
<tr>
<td>Korrespondenz/ExtraDatum2</td>
<td>Entsprechendes Feld auf der Korrespondenz</td>
</tr>
<tr>
<td>Korrespondenz/ExtraZahl2</td>
<td>Entsprechendes Feld auf der Korrespondenz</td>
</tr>
<tr>
<td>Korrespondenz/P2P-KampagneTitel</td>
<td>Falls das Ereignis der Korrespondenz mit einem Anlass verbunden ist, wird hier                 der Titel exportiert.</td>
</tr>
<tr>
<td>Korrespondenz/P2P-Initiators</td>
<td>Falls das Ereignis der Korrespondenz mit einem Anlass verbunden ist, wird hier                 der Initiator exportiert.</td>
</tr>
</tbody>
</table>
<h3 id="_2-2-5-zusagenfakturierung" tabindex="-1"><a class="header-anchor" href="#_2-2-5-zusagenfakturierung"><span>2.2.5. Zusagenfakturierung</span></a></h3>
<p>Die Zusagenfakturierung hat folgende Felder:</p>
<table>
<thead>
<tr>
<th>Feld</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>Inhalt/EsrZusageJahr/Betrag/Wert</td>
<td>Der Jahresbetrag</td>
</tr>
<tr>
<td>Inhalt/EsrZusageJahr/Betrag/Währung</td>
<td>Währung (z.B. &quot;CHF&quot;)</td>
</tr>
<tr>
<td>Inhalt/EsrZusageJahr/Esr</td>
<td>ESR Teilnehmer Nummer (5 stellig) zum Jahresbetrag</td>
</tr>
<tr>
<td>Inhalt/EsrZusageJahr/Konto</td>
<td>Konto</td>
</tr>
<tr>
<td>Inhalt/EsrZusageJahr/Refnum</td>
<td>Die Referenznummer</td>
</tr>
<tr>
<td>Inhalt/EsrZusageTeil/Betrag/Wert</td>
<td>Der Betrag zum Druck auf den ESR</td>
</tr>
<tr>
<td>Inhalt/EsrZusageTeil/Betrag/Währung</td>
<td>Währung (z.B. &quot;CHF&quot;)</td>
</tr>
<tr>
<td>Inhalt/EsrZusageTeil/Esr</td>
<td>ESR Teilnehmer Nummer (5 stellig)</td>
</tr>
<tr>
<td>Inhalt/EsrZusageTeil/Konto</td>
<td>Konto</td>
</tr>
<tr>
<td>Inhalt/EsrZusageTeil/Refnum</td>
<td>Die Referenznummer</td>
</tr>
<tr>
<td>Inhalt/EsrZusageOffen/Betrag/Wert</td>
<td>Der Betrag der offen ist</td>
</tr>
<tr>
<td>Inhalt/EsrZusageOffen/Betrag/Währung</td>
<td>Währung (z.B. &quot;CHF&quot;)</td>
</tr>
<tr>
<td>Inhalt/EsrZusageOffen/Esr</td>
<td>ESR Teilnehmer Nummer (5 stellig) zu offenem Betrag</td>
</tr>
<tr>
<td>Inhalt/EsrZusageOffen/Konto</td>
<td>Konto</td>
</tr>
<tr>
<td>Inhalt/EsrZusageOffen/Refnum</td>
<td>Die Referenznummer</td>
</tr>
<tr>
<td>Inhalt/EsrSpende/Betrag/Wert</td>
<td>Der Betrag der Spende</td>
</tr>
<tr>
<td>Inhalt/EsrSpende/Betrag/Währung</td>
<td>Währung (z.B. &quot;CHF&quot;)</td>
</tr>
<tr>
<td>Inhalt/EsrSpende/Esr</td>
<td>ESR Teilnehmer Nummer (5 stellig) zu der Spende</td>
</tr>
<tr>
<td>Inhalt/EsrSpende/Konto</td>
<td>Konto</td>
</tr>
<tr>
<td>Inhalt/EsrSpende/Refnum</td>
<td>Die Referenznummer</td>
</tr>
<tr>
<td>Inhalt/DokumentCode</td>
<td>Der Dokumentencode wie es in der Zusagenfakturierung definiert ist.</td>
</tr>
<tr>
<td>Inhalt/Datum</td>
<td>Datum wann der Export gestartet wurde</td>
</tr>
<tr>
<td>Inhalt/FakturaDatum</td>
<td>Datum wann die Fakturierung gestartet wurde</td>
</tr>
<tr>
<td>Inhalt/JobId</td>
<td>Job-ID aus Zusagenfakturierung-Kopf</td>
</tr>
<tr>
<td>Inhalt/ErhoDatum</td>
<td>Fakturadatum aus Zusagenfakturierung-Kopf</td>
</tr>
<tr>
<td>Inhalt/BriefDatum</td>
<td>&quot;Datum auf Brief&quot; aus Zusagenfakturierung-Position</td>
</tr>
<tr>
<td>Inhalt/ZusagenText</td>
<td>Zusagentext oder Beschreibung zu der Zusage</td>
</tr>
<tr>
<td>Inhalt/ExtraDatum1</td>
<td>Fakturadatum, falls es sich um die Zasage &quot;Fördermitgliedern&quot; handelt, dann                 Fakturadatum + 3 Jahre</td>
</tr>
<tr>
<td>Inhalt/ExtraText1</td>
<td>Bezeichnung der Zusage. Für Zusage &quot;Mitgliedschaft&quot; kommt &quot;EINZELN&quot;, &quot;FAMILIE&quot;                 oder &quot;KOLLEKTIV&quot;</td>
</tr>
<tr>
<td>Inhalt/ExtraText2</td>
<td>Der Dokumentencode</td>
</tr>
<tr>
<td>Inhalt/ExtraText3</td>
<td>2 Zeilige Adresse, 1.-te Zeile</td>
</tr>
<tr>
<td>Inhalt/ExtraText4</td>
<td>2 Zeilige Adresse, 2.-te Zeile</td>
</tr>
<tr>
<td>Inhalt/ExtraZahl1</td>
<td>Anzahl ESR</td>
</tr>
</tbody>
</table>
<h3 id="_2-2-6-briefadresse" tabindex="-1"><a class="header-anchor" href="#_2-2-6-briefadresse"><span>2.2.6. Briefadresse</span></a></h3>
<p>Briefadresse hat folgende Felder:</p>
<table>
<thead>
<tr>
<th>Feld</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>Person/AdressNummer</td>
<td>Personen-ID</td>
</tr>
<tr>
<td>Person/AdressZeilen32</td>
<td>Adresse mit Breite von 32 Zeichen</td>
</tr>
<tr>
<td>Person/AdressZeilenUnb</td>
<td>Adresse ohne Breitenbegrenzung</td>
</tr>
<tr>
<td>Person/Anrede</td>
<td>Anrede</td>
</tr>
<tr>
<td>Person/Name-Firma</td>
<td>Name oder Firma</td>
</tr>
<tr>
<td>Person/Vorname</td>
<td>Vorname</td>
</tr>
<tr>
<td>Person/Name</td>
<td>Nachname</td>
</tr>
<tr>
<td>Person/SprachCode</td>
<td>Sprache</td>
</tr>
<tr>
<td>Person/State</td>
<td>Status</td>
</tr>
</tbody>
</table>
<h3 id="_2-2-7-beschenkte" tabindex="-1"><a class="header-anchor" href="#_2-2-7-beschenkte"><span>2.2.7. Beschenkte</span></a></h3>
<p>Beschenkte hat folgende Felder:</p>
<table>
<thead>
<tr>
<th>Feld</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>Beschenkte/Person/AdressNummer</td>
<td>Personen-ID</td>
</tr>
<tr>
<td>Beschenkte/Person/AdressZeilen32</td>
<td>Adresse mit Breite von 32 Zeichen</td>
</tr>
<tr>
<td>Beschenkte/Person/AdressZeilenUnb</td>
<td>Adresse ohne Breitenbegrenzung</td>
</tr>
<tr>
<td>Beschenkte/Person/Anrede</td>
<td>Anrede</td>
</tr>
<tr>
<td>Beschenkte/Person/Name-Firma</td>
<td>Name oder Firma</td>
</tr>
<tr>
<td>Beschenkte/Person/Vorname</td>
<td>Vorname</td>
</tr>
<tr>
<td>Beschenkte/Person/Name</td>
<td>Nachname</td>
</tr>
<tr>
<td>Beschenkte/Person/SprachCode</td>
<td>Sprache</td>
</tr>
<tr>
<td>Beschenkte/Person/State</td>
<td>Status</td>
</tr>
</tbody>
</table>
<h3 id="_2-2-8-drittzahler" tabindex="-1"><a class="header-anchor" href="#_2-2-8-drittzahler"><span>2.2.8. Drittzahler</span></a></h3>
<p>Drittzahler hat folgende Felder:</p>
<table>
<thead>
<tr>
<th>Feld</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>Drittzahler/Person/AdressNummer</td>
<td>Personen-ID</td>
</tr>
<tr>
<td>Drittzahler/Person/AdressZeilen32</td>
<td>Adresse mit Breite von 32 Zeichen</td>
</tr>
<tr>
<td>Drittzahler/Person/AdressZeilenUnb</td>
<td>Adresse ohne Breitenbegrenzung</td>
</tr>
<tr>
<td>Drittzahler/Person/Anrede</td>
<td>Anrede</td>
</tr>
<tr>
<td>Drittzahler/Person/Name-Firma</td>
<td>Name oder Firma</td>
</tr>
<tr>
<td>Drittzahler/Person/Vorname</td>
<td>Vorname</td>
</tr>
<tr>
<td>Drittzahler/Person/Name</td>
<td>Nachname</td>
</tr>
<tr>
<td>Drittzahler/Person/SprachCode</td>
<td>Sprache</td>
</tr>
<tr>
<td>Drittzahler/Person/State</td>
<td>Status</td>
</tr>
</tbody>
</table>
<h3 id="_2-2-8-veranstaltungen" tabindex="-1"><a class="header-anchor" href="#_2-2-8-veranstaltungen"><span>2.2.8. Veranstaltungen</span></a></h3>
<p>Veranstaltung hat folgende Felder/Tags:</p>
<table>
<thead>
<tr>
<th>Feld/Tag</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>Person</td>
<td>Alle Daten der Adresse für Fakturierung</td>
</tr>
<tr>
<td>Beschenkte/Person</td>
<td>Alle Daten für Teilnehmer im Struktur Person</td>
</tr>
<tr>
<td>Members/Person</td>
<td>Alle Daten für alle Teilnehmer der Gruppe im Struktur Person</td>
</tr>
<tr>
<td>Inhalt/EsrData1</td>
<td>Esr-Daten mit fakturiertem Betrag</td>
</tr>
<tr>
<td>Inhalt/EsrData2</td>
<td>Esr-Daten mit offenem Betrag</td>
</tr>
<tr>
<td>Inhalt/ExportTyp</td>
<td>Kann &quot;Faktura&quot; oder &quot;Storno&quot; sein</td>
</tr>
<tr>
<td>Inhalt/Fakturanummer</td>
<td>Fakturanummer</td>
</tr>
<tr>
<td>Inhalt/Jahr</td>
<td>Jahr aus Veranstaltung</td>
</tr>
<tr>
<td>Inhalt/Veranstaltung</td>
<td>Name aus Veranstaltung</td>
</tr>
<tr>
<td>Inhalt/VeranstaltungsTyp</td>
<td>Veranstaltungstyp</td>
</tr>
<tr>
<td>Inhalt/VeranstaltungsBeschreibung</td>
<td>Beschreibung aus Veranstaltungskopf</td>
</tr>
<tr>
<td>Inhalt/VonDatum</td>
<td>&quot;Datum von&quot; aus Veranstaltungskopf</td>
</tr>
<tr>
<td>Inhalt/BisDatum</td>
<td>&quot;Datum bis&quot; aus Veranstaltungskopf</td>
</tr>
<tr>
<td>Inhalt/VeranstaltungsBetrag</td>
<td>Betrag der Veranstaltung</td>
</tr>
<tr>
<td>Inhalt/RechnungBetrag</td>
<td>Betrag (Veranstaltung + Spende + Guthaben)</td>
</tr>
<tr>
<td>Inhalt/RechnungsSpende</td>
<td>Spende</td>
</tr>
<tr>
<td>Inhalt/RechnungGuthaben</td>
<td>Guthaben</td>
</tr>
<tr>
<td>Inhalt/LagerName</td>
<td>Name aus Veranstaltungstyp</td>
</tr>
<tr>
<td>Inhalt/LagerBetrag</td>
<td>Betrag aus Veranstaltungskopf</td>
</tr>
<tr>
<td>Inhalt/MahnBetrag</td>
<td>gemahnter Betrag</td>
</tr>
<tr>
<td>Inhalt/AnmeldeStatus</td>
<td>Anmeldestatus</td>
</tr>
<tr>
<td>Inhalt/VerfalDatum</td>
<td>Bis wann der Betrag bezahlt sein sollte</td>
</tr>
<tr>
<td>Inhalt/Scharf</td>
<td>Schärfe für Mahnung</td>
</tr>
<tr>
<td>Inhalt/RechnungsDatum</td>
<td>Rechnungdatum</td>
</tr>
<tr>
<td>Inhalt/MahnungsDatum</td>
<td>Mahnungsdatum</td>
</tr>
<tr>
<td>Inhalt/JobDatum</td>
<td>Datum des Jobs</td>
</tr>
<tr>
<td>Inhalt/DokumentCode</td>
<td>Dokumentencode</td>
</tr>
<tr>
<td>Inhalt/LandTyp</td>
<td>Kann &quot;Inland&quot; oder &quot;Ausland&quot; sein. Ist von der Adresse für Fakturierung                 berechnet</td>
</tr>
</tbody>
</table>
<h2 id="_2-3-siehe-auch" tabindex="-1"><a class="header-anchor" href="#_2-3-siehe-auch"><span>2.3. Siehe auch</span></a></h2>
<p><RouteLink to="/referenz/refs/ft.refdata.do-attribute.html" title="Attribute">Attribute</RouteLink></p>
<p><RouteLink to="/referenz/refs/ft.refdata.do-dokumente.html" title="Dokumente, Stammtabelle">Dokumente</RouteLink></p>
<p><RouteLink to="/referenz/refs/ft.refdata.do-doksatz.html" title="Dokumententensatz">Dokumentensätze</RouteLink></p>
<p><RouteLink to="/referenz/refs/ft.refdata.do-outputverwaltung.html" title="Outputverwaltung">Outputverwaltung</RouteLink></p>
<p><RouteLink to="/referenz/refs/ft.refdata.do-text.html" title="Text- und Bildelemente">Texte</RouteLink></p>
<p><RouteLink to="/referenz/refs/ft.refdata.do-vorlagen.html" title="Vorlagen">Vorlagen</RouteLink></p>
</div></template>


